import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { normalizePhoneInput } from "@/lib/digits";
import {
  countryList, examplePlaceholder, formatNationalDigits,
  splitPhone, joinPhone,
} from "@/lib/phone";

export { splitPhone, joinPhone };

/* حقل هاتف دولي ذكي: محدد دولة قابل للبحث (علم + اسم + مفتاح) + رقم محلي منسق.
   value/onChange يتعاملان مع الرقم الكامل E.164 (+9665XXXXXXXX).
   defaultCc: مفتاح دولة التوصيل (من قاعدة البيانات) — يُستخدم عندما يكون الحقل فارغاً.
   onManualCc: يُستدعى عند تغيير المفتاح يدوياً حتى لا يُستبدل اختيار العميل تلقائياً. */
export default function PhoneField({ value, onChange, lang = "ar", testId = "phone-field",
                                     placeholder, defaultCc = "", onManualCc, onBlur }) {
  const ar = lang === "ar";
  const [pickedCc, setPickedCc] = useState("");
  const { cc: valCc, num } = useMemo(
    () => splitPhone(value, pickedCc || defaultCc || "974"),
    [value, pickedCc, defaultCc]);
  const cc = value ? valCc : (pickedCc || defaultCc || "974");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);

  const list = useMemo(() => countryList(lang), [lang]);
  const sel = useMemo(() => list.find((c) => c.dial === String(cc)), [list, cc]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase().replace(/^\+/, "");
    if (!s) return list;
    return list.filter((c) =>
      c.name.toLowerCase().includes(s) || c.en.toLowerCase().includes(s) ||
      c.dial.startsWith(s) || c.iso.toLowerCase() === s);
  }, [q, list]);

  const openPanel = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const w = Math.min(300, window.innerWidth - 16);
      let left = r.left;
      if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;
      setPos({ top: Math.min(r.bottom + 6, Math.max(60, window.innerHeight - 330)), left: Math.max(8, left), width: w });
    }
    setQ("");
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (c) => {
    setPickedCc(c.dial);
    setOpen(false);
    setQ("");
    if (onManualCc) onManualCc(c.dial);
    if (num) onChange(joinPhone(c.dial, num));
  };

  const display = formatNationalDigits(cc, num);
  const ph = placeholder || examplePlaceholder(cc) || (ar ? "رقم الجوال" : "Phone number");

  return (
    <div className="flex gap-1.5 flex-1 min-w-0" dir="ltr">
      <button type="button" ref={btnRef} onClick={() => (open ? setOpen(false) : openPanel())}
              className="input-field !w-[106px] shrink-0 !px-2 text-sm flex items-center justify-center gap-1"
              aria-haspopup="listbox" aria-expanded={open}
              data-testid={`${testId}-cc`}>
        <span className="text-base leading-none">{sel?.flag || "🌐"}</span>
        <span className="font-semibold" dir="ltr">+{cc}</span>
        <ChevronDown size={12} className={`opacity-50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && pos && (
        <div ref={panelRef} className="fixed z-[120] rounded-2xl bg-white shadow-2xl border overflow-hidden"
             style={{ top: pos.top, left: pos.left, width: pos.width, borderColor: "var(--border, #e5e0d5)" }}
             role="listbox" data-testid={`${testId}-cc-panel`}>
          <div className="flex items-center gap-2 px-3 border-b" style={{ borderColor: "var(--border, #e5e0d5)" }}>
            <Search size={14} className="opacity-40 shrink-0" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                   className="w-full py-2.5 text-sm outline-none bg-transparent"
                   placeholder={ar ? "ابحث عن الدولة…" : "Search country…"}
                   dir={ar ? "rtl" : "ltr"}
                   data-testid={`${testId}-cc-search`} />
          </div>
          <div className="max-h-[256px] overflow-y-auto overscroll-contain">
            {filtered.map((c) => (
              <button key={c.iso} type="button" onClick={() => pick(c)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-black/5 transition-colors ${sel?.iso === c.iso ? "bg-black/5 font-semibold" : ""}`}
                      dir={ar ? "rtl" : "ltr"}
                      role="option" aria-selected={sel?.iso === c.iso}
                      data-testid={`${testId}-cc-option-${c.iso}`}>
                <span className="text-base leading-none shrink-0">{c.flag}</span>
                <span className="flex-1 min-w-0 truncate text-start">{c.name}</span>
                <span className="opacity-55 shrink-0 text-xs font-semibold" dir="ltr">+{c.dial}</span>
              </button>
            ))}
            {!filtered.length && (
              <div className="p-4 text-center text-xs opacity-60">{ar ? "لا توجد نتائج" : "No results"}</div>
            )}
          </div>
        </div>
      )}
      <input className="input-field flex-1 min-w-0" inputMode="tel" dir="ltr"
             value={display}
             onChange={(e) => {
               const raw = normalizePhoneInput(e.target.value);
               // لصق رقم دولي كامل (+ أو 00) → يُفكك ويُحدَّث المفتاح تلقائياً
               if (raw.includes("+") || raw.replace(/[^0-9]/g, "").startsWith("00")) {
                 const s = splitPhone(raw, cc);
                 setPickedCc(s.cc);
                 onChange(joinPhone(s.cc, s.num));
                 return;
               }
               let digits = raw.replace(/\D/g, "");
               // حذف مسافة تنسيق فقط → احذف الرقم الذي قبلها حتى لا يعلق المؤشر
               if (digits === num && e.target.value.length < display.length) digits = digits.slice(0, -1);
               onChange(joinPhone(cc, digits));
             }}
             onBlur={onBlur}
             placeholder={ph}
             data-testid={`${testId}-num`} />
    </div>
  );
}
